import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { faFilter, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { SearchField } from "@/components/SearchField/SearchField";
import { classNames } from "@/utils/classNames";

import { SortByDropdown } from "@/pages/Private/pages/List/components/SortByDropdown/SortByDropdown";
import { SortOption } from "@/pages/Private/pages/List/components/SortByDropdown/sort-by-dropdown.types";
import { useSortUrlSync } from "@/pages/Private/pages/List/components/SortByDropdown/useSortUrlSync";

export interface MessagesFilterBarProps {
	/** Current search text. */
	searchValue: string;
	/** Called with the new search text on change / reset. */
	onSearchChange: (value: string) => void;
	searchPlaceholder?: string;
	/** Number of active advanced filters — shown as a badge on the Filter button. */
	filterCount: number;
	/** Opens the advanced FilterModal. */
	onOpenFilter: () => void;
	/** Optional leading control rendered at the far left (e.g. the lead-list collapse toggle). */
	leading?: ReactNode;
	/** Optional control rendered right after the search field, before the dropdowns (e.g. a view switcher). */
	afterSearch?: ReactNode;
	/** Page-owned dropdowns (Sender / Status / Score …). */
	dropdowns?: ReactNode;
	/** Optional trailing content (e.g. the Outbox "N leads selected" text). */
	trailing?: ReactNode;
	/** Surface-specific extra "Most used" sorts for the Sort by dropdown (e.g. Inbox "Best reply"). */
	sortExtraOptions?: SortOption[];
	className?: string;
}

/**
 * Horizontal filter bar shared by the Messages pages (Inbox, Outbox,
 * ReadyToSend, Compose). Renders an always-visible search field, page-specific
 * dropdowns, and a Filter button pushed to the far right. Sized to match the
 * list-detail (UnassignedTable) toolbar: a 44px row with 32px controls. Purely
 * presentational — each page keeps its own filter state and handlers.
 */
export const MessagesFilterBar: FunctionComponent<MessagesFilterBarProps> = ({
	searchValue,
	onSearchChange,
	searchPlaceholder,
	filterCount,
	onOpenFilter,
	leading,
	afterSearch,
	dropdowns,
	trailing,
	sortExtraOptions,
	className,
}) => {
	const { t } = useTranslation();
	const { sortBy, sortOrder, onSort } = useSortUrlSync();

	return (
		<div
			className={classNames(
				"@container flex flex-wrap items-center gap-2 min-h-11 py-1.5 w-full px-4 bg-white",
				className
			)}
		>
			{leading}
			{/* PREC-4217: on a laptop the bar was wider than its column and Sort by / Filter were
			    cut off. The search starts at its narrow width and grows into spare room, so it
			    never forces a wrap; the trailing caption is hidden when the bar is narrow; Sort by
			    and Filter never shrink. Only when all of that is not enough does the bar wrap. */}
			<div className="basis-[140px] grow max-w-[280px] min-w-[140px]">
				<SearchField
					className="!top-[9px] !left-[12px]"
					classNameInput="!h-[32px] !rounded-[10px] !text-xs !pl-9"
					handleChange={(e) => onSearchChange(e.target.value)}
					handleReset={() => onSearchChange("")}
					icon={<Icon className="h-[14px]" icon={faSearch} />}
					iconPosition="left"
					name="messages-filter-search"
					placeholder={
						searchPlaceholder ?? t("basics.search", { something: t("sidebar.prospects") })
					}
					value={searchValue}
				/>
			</div>

			{afterSearch}

			{dropdowns}

			<div className="ml-auto flex min-w-0 items-center gap-2">
				{trailing ? (
					<div
						className="hidden @[1000px]:block min-w-0 truncate"
						data-testid="messages-filter-trailing"
					>
						{trailing}
					</div>
				) : null}
				<div className="flex shrink-0 items-center gap-2">
					<SortByDropdown
						extraMostUsed={sortExtraOptions}
						onSort={onSort}
						sortBy={sortBy}
						sortOrder={sortOrder}
						testId="messages-sort-by"
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="mr-2" icon={faFilter} />}
						size={ButtonSize.S}
						title={`${t("basics.filter")}${filterCount > 0 ? ` (${filterCount})` : ""}`}
						onClick={onOpenFilter}
					/>
				</div>
			</div>
		</div>
	);
};
